import React from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';
import { graphql, createRefetchContainer } from 'react-relay';
import serverVars from 'server-vars';
import { VisitorTracking, trackPreRegistrationPageVisit } from 'dibs-visit-tracking';
import { CUSTOMER_TYPE_TRADE } from '../constants/userConstants';

// components
import PdpRespContainer from './PdpRespContainer/PdpRespContainer';
import PdpRespContainerNotListed from './PdpRespContainer/PdpRespContainerNotListed';
import SharedCurrencyLoader from '../shared/SharedCurrencyLoader';
import SharedUserAndRelayVarLoader from '../shared/SharedUserAndRelayVarLoader';

// redux actions
import { readyToFireInitialTracking } from '../actions/trackingRespActions';

// helpers
import copy from '../layouts/layoutCopy.json';
import get from 'lodash.get';
import { PdpHead } from './PdpHead';
import './styles/PdpRespLayout.scss';
import tracking from 'dibs-tracking';
import { trackEcommerce } from './pdpTracking';

export class PdpRespLayout extends React.Component {
    constructor(props) {
        super(props);

        this.fireInitialTracking = this.fireInitialTracking.bind(this);
        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.getFollowSearchData = this.getFollowSearchData.bind(this);

        this.initialTrackingHasFired = false;
        this.relayVars = {};
    }

    componentDidMount() {
        if (this.props.isPdpRespPreviewPage) {
            document.body.classList.add('disable-click-on-preview');
        }
    }

    componentDidUpdate() {
        const { waitToFireInitialTracking } = this.props;
        if (waitToFireInitialTracking && this.getTrackingData()) {
            this.props.readyToFireInitialTracking();
        } else if (!waitToFireInitialTracking && !this.initialTrackingHasFired) {
            this.initialTrackingHasFired = true;
            this.fireInitialTracking();
        }
    }

    doRefetch(variables, renderVariables, callback) {
        this.relayVars = { ...this.relayVars, ...variables };
        this.props.relay.refetch(
            this.relayVars,
            { ...this.relayVars, ...renderVariables, fetchPdp: true },
            callback
        );
    }

    fireInitialTracking() {
        const { item } = this.props.viewer || {};
        const { url: opt_pageURL, ...additional } = this.getTrackingData() || {}; // eslint-disable-line camelcase

        if (get(serverVars, 'youMayAlsoLikeNC') === 'test') {
            additional.cdnTest2 = 'youMayAlsoLike - test';
        } else if (get(serverVars, 'youMayAlsoLikeNC') === 'control') {
            additional.cdnTest2 = 'youMayAlsoLike - control';
        }

        if (get(serverVars, 'landingSession') === true) {
            additional.cdnTest6 = 'itemsSimilarTo - true';
        }

        additional.hasViewInRoom = get(item, 'canShowViewInRoom');

        // page view
        tracking.trackPageview({ opt_pageURL, additional }); // eslint-disable-line camelcase
        // ecommerce product detail
        trackEcommerce(item);

        new VisitorTracking();
        trackPreRegistrationPageVisit();
    }

    getFollowSearchData() {
        const searchPage = get(this.props, 'viewer.item.saveSearchData.page');
        return { favoritePages: [searchPage], fetchFavorites: !!searchPage };
    }

    getTrackingData() {
        return get(this.props, 'viewer.item.pdpTrackingVpvParams') || null;
    }

    handleUserRefetch(variables, ...args) {
        /**
         * Need to refetch pdp items for trade users, the price depends on the `isTrade` variable
         * being set.
         *
         * Only do this after login, hence the server vars check, subsequent visits will depend on
         * the ut query param.
         */
        const fetchPdp = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        this.doRefetch({ ...variables, fetchPdp }, ...args);
    }

    render() {
        const pdpTypeDisplay = get(this.props, 'viewer.item.pdpTypeDisplay');
        const pdpHead = new PdpHead(this.props);
        const viewer = get(this.props, 'viewer', null);
        const user = get(viewer, 'user', null);
        const item = get(viewer, 'item', null);

        const sharedProps = {
            currency: this.props.currency,
            relayVariables: this.props.relayVariables,
            viewer,
            user,
            item,
            similarGridItems: get(item, 'similarGridItems', null),
            hasFetchedOnClient: !!get(viewer, 'hasFetchedOnClient'),
        };

        return (
            <React.Fragment>
                <Helmet
                    title={copy.title}
                    meta={[{ name: 'description', content: copy.description }]}
                    link={[{ rel: 'dns-prefetch', href: process.env.NODE_CDN_HOST }]}
                />
                <SharedUserAndRelayVarLoader
                    onUserRefetch={this.handleUserRefetch}
                    getAdditionalRelayVariables={this.getFollowSearchData}
                />
                <SharedCurrencyLoader
                    viewer={viewer}
                    user={user}
                    forcedCurrency={this.props.forcedCurrency}
                />
                <Helmet
                    title={pdpHead.title()}
                    meta={pdpHead.meta()}
                    link={[...pdpHead.link()]}
                    script={[pdpHead.microdata()]}
                />
                {pdpTypeDisplay === 'NOT_LISTED' && !this.props.isPdpRespPreviewPage ? (
                    // Unlisted Item Pdp
                    <PdpRespContainerNotListed {...sharedProps} />
                ) : (
                    // Default Pdp
                    <PdpRespContainer
                        {...sharedProps}
                        displayInfo={get(viewer, 'item.displayInfo', null)}
                        sellerItems={get(viewer, 'item.sellerItems', null)}
                        similarLandingItems={get(viewer, 'item.similarLandingItems', null)}
                        vipUserRelationship={get(viewer, 'vipUserRelationships[0]', null)}
                        localDeliveryOptions={get(viewer, 'localDeliveryOptions', null)}
                        domesticDeliveryOptions={get(viewer, 'domesticDeliveryOptions', null)}
                    />
                )}
            </React.Fragment>
        );
    }
}

PdpRespLayout.propTypes = {
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        newAndCustomSplitTest: PropTypes.bool,
        isClient: PropTypes.bool.isRequired,
        fetchUser: PropTypes.bool.isRequired,
        relationshipUserIds: PropTypes.array.isRequired,
        favoritePages: PropTypes.array,
        fetchFavorites: PropTypes.bool.isRequired,
    }).isRequired,
    viewer: PropTypes.object.isRequired,
    intl: intlShape.isRequired,
    relay: PropTypes.object.isRequired,
    forcedCurrency: PropTypes.string,

    // from redux
    currency: PropTypes.string,
    isPdpRespPreviewPage: PropTypes.bool,
    waitToFireInitialTracking: PropTypes.bool,
    readyToFireInitialTracking: PropTypes.func,
    showSimilarItemsTop: PropTypes.func,
};

// Redux Map Functions
function mapStateToProps({
    currencyReducer,
    trackingReducer,
    intlVariables,
    pdpPreviewPage,
    relayVariables,
}) {
    return {
        currency: currencyReducer.currency,
        waitToFireInitialTracking: trackingReducer.waitToFireInitialTracking,
        isPdpRespPreviewPage: pdpPreviewPage.isPdpRespPreviewPage,
        intlVariables,
        relayVariables: relayVariables.variables,
    };
}
function mapDispatchToProps(dispatch) {
    return {
        readyToFireInitialTracking: () => dispatch(readyToFireInitialTracking()),
    };
}

const PdpRespLayoutContainer = createRefetchContainer(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(PdpRespLayout),
    {
        viewer: graphql`
            fragment PdpRespLayout_viewer on Viewer
                @argumentDefinitions(fetchPdp: { type: "Boolean", defaultValue: true }) {
                ...PdpRespContainer_viewer
                ...PdpRespContainerNotListed_viewer
                ...PdpHead_viewer @relay(mask: false)
                ...SharedCurrencyLoader_viewer
                ... on Viewer @include(if: $isClient) {
                    hasFetchedOnClient: __typename
                }
                user(userId: $userId) @include(if: $fetchUser) {
                    ...SharedCurrencyLoader_user
                    ...PdpRespContainer_user
                    ...PdpRespContainerNotListed_user
                }
                item(itemId: $itemId) @include(if: $fetchPdp) {
                    ...PdpHead_item @relay(mask: false)
                    ...PdpRespContainer_item
                    ...PdpRespContainerNotListed_item
                    saveSearchData {
                        page
                    }
                    canShowViewInRoom
                    contemporaryTrackingString
                    displayInfo(page: pdp, buyerId: $userId) @include(if: $isClient) {
                        ...PdpRespContainer_displayInfo
                    }
                    seller {
                        serviceId
                    }
                    sellerItems: moreLikeThis(first: 4, seller: INCLUDE) {
                        ...PdpRespContainer_sellerItems
                    }
                    similarLandingItems: moreLikeThis(
                        first: 12
                        isTrade: $isTrade
                        priceRangePercentage: "5-15"
                    ) {
                        ...PdpRespContainer_similarLandingItems
                    }
                    similarGridItems: moreLikeThis(
                        first: 8
                        seller: EXCLUDE
                        newAndCustomSplitTest: $newAndCustomSplitTest
                        isTrade: $isTrade
                        priceRangePercentage: "5-15"
                    ) {
                        ...PdpRespContainer_similarGridItems
                        ...PdpRespContainerNotListed_similarGridItems
                    }
                    pdpTypeDisplay
                    pdpTrackingVpvParams(userId: $userId) @include(if: $isClient)
                    attributes {
                        period {
                            option {
                                name
                            }
                        }
                    }
                    browseUrl
                    categoryCode
                }
                vipUserRelationships: userRelationships(
                    userIds: $relationshipUserIds
                    relationshipType: VIP
                ) @include(if: $fetchUser) {
                    ...PdpRespContainer_vipUserRelationship
                }
                localDeliveryOptions: availableDeliveryOptions(
                    itemId: $itemId
                    regionView: LOCAL
                    first: 10
                    addressResolutionType: IP
                ) @include(if: $isClient) {
                    ...PdpRespContainer_localDeliveryOptions
                }
                domesticDeliveryOptions: availableDeliveryOptions(
                    itemId: $itemId
                    regionView: DOMESTIC
                    first: 10
                    addressResolutionType: IP
                ) @include(if: $isClient) {
                    ...PdpRespContainer_domesticDeliveryOptions
                }
            }
        `,
    },
    graphql`
        query PdpRespLayoutRefetchQuery(
            $newAndCustomSplitTest: Boolean
            $isClient: Boolean!
            $fetchPdp: Boolean!
            $itemId: String!
            $userId: String = ""
            $fetchUser: Boolean!
            $relationshipUserIds: [String]!
            $favoritePages: [String]
            $fetchFavorites: Boolean!
            $isTrade: Boolean!
        ) {
            viewer {
                ... on Viewer @include(if: $isClient) {
                    hasFetchedOnClient: __typename
                }
                ...PdpRespContainer_viewer
                ...PdpRespContainerNotListed_viewer
                ...PdpHead_viewer @relay(mask: false) @skip(if: $fetchPdp)
                ...SharedCurrencyLoader_viewer @skip(if: $fetchPdp)
                user(userId: $userId) @include(if: $fetchUser) {
                    ...SharedCurrencyLoader_user
                    ...PdpRespContainer_user
                    ...PdpRespContainerNotListed_user
                }
                item(itemId: $itemId) {
                    ...PdpHead_item @relay(mask: false)
                    ...PdpRespContainer_item
                    ...PdpRespContainerNotListed_item
                    saveSearchData {
                        page
                    }
                    canShowViewInRoom
                    contemporaryTrackingString
                    displayInfo(page: pdp, buyerId: $userId) @include(if: $isClient) {
                        ...PdpRespContainer_displayInfo
                    }
                    sellerItems: moreLikeThis(first: 4, seller: INCLUDE) {
                        ...PdpRespContainer_sellerItems
                    }
                    similarLandingItems: moreLikeThis(
                        first: 12
                        isTrade: $isTrade
                        priceRangePercentage: "5-15"
                    ) {
                        ...PdpRespContainer_similarLandingItems
                    }
                    similarGridItems: moreLikeThis(
                        first: 8
                        seller: EXCLUDE
                        newAndCustomSplitTest: $newAndCustomSplitTest
                        isTrade: $isTrade
                        priceRangePercentage: "5-15"
                    ) {
                        ...PdpRespContainer_similarGridItems
                        ...PdpRespContainerNotListed_similarGridItems
                    }
                    pdpTypeDisplay
                    pdpTrackingVpvParams(userId: $userId) @include(if: $isClient)
                    attributes {
                        period {
                            option {
                                name
                            }
                        }
                    }
                    browseUrl
                    categoryCode
                }
                vipUserRelationships: userRelationships(
                    userIds: $relationshipUserIds
                    relationshipType: VIP
                ) @include(if: $fetchUser) {
                    ...PdpRespContainer_vipUserRelationship
                }
                localDeliveryOptions: availableDeliveryOptions(
                    itemId: $itemId
                    regionView: LOCAL
                    first: 10
                    addressResolutionType: IP
                ) @include(if: $isClient) {
                    ...PdpRespContainer_localDeliveryOptions
                }
                domesticDeliveryOptions: availableDeliveryOptions(
                    itemId: $itemId
                    regionView: DOMESTIC
                    first: 10
                    addressResolutionType: IP
                ) @include(if: $isClient) {
                    ...PdpRespContainer_domesticDeliveryOptions
                }
            }
        }
    `
);

export default injectIntl(PdpRespLayoutContainer);
